import { useEffect } from "react";

/**
 * Custom hook to lock/unlock body scroll
 * Commonly used for modals, mobile menus, overlays, etc.
 *
 * @param isLocked - Whether body scroll should be locked
 *
 * @example
 * ```tsx
 * const MyModal = ({ isOpen }) => {
 *   useBodyScrollLock(isOpen);
 *
 *   return isOpen ? <div>Modal content</div> : null;
 * };
 * ```
 */
export function useBodyScrollLock(isLocked: boolean): void {
  useEffect(() => {
    if (isLocked) {
      // Store original styles
      const originalStyle = window.getComputedStyle(document.body).overflow;

      // Lock scroll
      document.body.style.overflow = "hidden";

      // Cleanup function
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
    return;
  }, [isLocked]);
}

/**
 * Enhanced version that also handles iOS Safari scroll issues
 * and maintains scroll position
 *
 * @param isLocked - Whether body scroll should be locked
 * @param restoreScrollPosition - Whether to restore scroll position when unlocked
 *
 * @example
 * ```tsx
 * useBodyScrollLockEnhanced(isMobileMenuOpen, true);
 * ```
 */
export function useBodyScrollLockEnhanced(
  isLocked: boolean,
  restoreScrollPosition: boolean = true
): void {
  useEffect(() => {
    if (!isLocked) return;

    // Store original values
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    const scrollY = restoreScrollPosition ? window.scrollY : 0;

    // Calculate scrollbar width to prevent layout shift
    const scrollBarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    // Apply lock styles
    document.body.style.overflow = "hidden";
    document.body.style.paddingRight =
      scrollBarWidth > 0 ? `${scrollBarWidth}px` : "";

    // For iOS Safari - prevent background scroll
    if (restoreScrollPosition) {
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
    }

    // Cleanup function
    return () => {
      // Restore original styles
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;

      if (restoreScrollPosition) {
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";

        // Restore scroll position
        window.scrollTo(0, scrollY);
      }
    };
  }, [isLocked, restoreScrollPosition]);
}
